// The on-screen AGENDA (Wes, Sept 28 2026): the consult's parts from config,
// the current one highlighted from the server's agenda index, and the clock as
// a gentle guide, never a countdown alarm. Near the target, when Otto asks
// whether the owner has time to keep going, the owner can answer out loud or
// tap here; the owner decides.
import { useEffect, useState } from "react";
import type { AgendaItemView } from "@otto/protocol";
import type { ConsultStrings } from "../strings";

export interface AgendaClock {
  seconds: number;
  /** performance.now() when `seconds` arrived. */
  receivedAt: number;
  paused: boolean;
}

/** Minutes to show: the server's connected seconds, advanced locally between updates unless paused. */
export function displayedMinutes(clock: AgendaClock, now: number): number {
  const seconds = clock.paused ? clock.seconds : clock.seconds + Math.max(0, (now - clock.receivedAt) / 1000);
  return Math.max(0, Math.floor(seconds / 60));
}

/** The guide line. Never a countdown, never red: minutes so far against the plan. */
export function clockLine(s: ConsultStrings, minutes: number, targetMinutes: number, extended: boolean): string {
  if (extended && minutes >= targetMinutes) return s.call.clockExtended(minutes);
  if (minutes >= targetMinutes) return s.call.clockPastTarget(minutes);
  return s.call.clockSoFar(minutes, Math.round(targetMinutes));
}

export interface AgendaPanelProps {
  s: ConsultStrings;
  items: AgendaItemView[];
  index: number;
  clock: AgendaClock;
  targetMinutes: number;
  extension: { offered: boolean; accepted: boolean | null; answered: boolean | null };
  onAnswer: (accept: boolean) => void;
  compact?: boolean;
  now?: () => number;
}

const perfNow = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

export function AgendaPanel({
  s,
  items,
  index,
  clock,
  targetMinutes,
  extension,
  onAnswer,
  compact = false,
  now = perfNow,
}: AgendaPanelProps) {
  const [tick, setTick] = useState(() => now());
  useEffect(() => {
    // A slow tick: the clock line changes once a minute, so this never animates.
    const timer = setInterval(() => setTick(now()), 5000);
    return () => clearInterval(timer);
  }, [now]);
  const minutes = displayedMinutes(clock, Math.max(tick, clock.receivedAt));
  const current = Math.min(Math.max(0, index), Math.max(0, items.length - 1));
  const extended = extension.accepted === true || extension.answered === true;
  const asking = extension.offered && extension.accepted === null && extension.answered === null;

  return (
    <section className={`consult-agenda${compact ? " is-compact" : ""}`} aria-labelledby="consult-agenda-title">
      <p className="consult-agenda__title" id="consult-agenda-title">
        {s.call.agendaTitle}
      </p>
      <ol className="consult-agenda__list">
        {items.map((item, position) => (
          <li
            key={item.id}
            className={position < current ? "is-done" : position === current ? "is-current" : "is-next"}
            aria-current={position === current ? "step" : undefined}
          >
            <span className="consult-agenda__dot" aria-hidden="true" />
            <span className="consult-agenda__label">{item.label}</span>
          </li>
        ))}
      </ol>
      <p className="consult-agenda__clock">{clockLine(s, minutes, targetMinutes, extended)}</p>
      {asking && (
        <div className="consult-agenda__ask" role="group" aria-label={s.call.extensionAsk}>
          <p>{s.call.extensionAsk}</p>
          <div>
            <button type="button" onClick={() => onAnswer(true)}>
              {s.call.extensionYes}
            </button>
            <button type="button" onClick={() => onAnswer(false)}>
              {s.call.extensionNo}
            </button>
          </div>
        </div>
      )}
      {extension.offered && !asking && extension.accepted === null && (
        <p className="consult-agenda__answered">{s.call.extensionThanks}</p>
      )}
    </section>
  );
}
